// Sidebar nav config — hierarchical, mirroring the PHP portal's nav
// groups (e.g. OLTs → OLT Issues; later: RADIUS → Status / RADIUS Issues
// / Box Chains, IP Phone → Status / Call Blasting). One entry can have
// `children` rendered indented below it; an entry with no `to` is a
// (children-only) group header. Gating a page = giving it a `permission`
// (and wrapping its route in <RequireAccess> — see router.tsx); the
// layout filters the tree by the current user's permissions via
// `useVisibleNav()`.

import {
  AlertTriangle,
  Boxes,
  Cable,
  Database,
  FileSpreadsheet,
  Gauge,
  GitBranch,
  Link2,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Network,
  PhoneCall,
  Router,
  Search,
  Server,
  Settings2,
  ShieldAlert,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { effectivePermissions, type Permission } from "@/features/auth/roles";

export interface NavItem {
  label: string;
  /** Route this links to. Omit for a non-link group header (children only). */
  to?: string;
  icon: LucideIcon;
  /** Required permission to see this item. Omitted = any authenticated user. */
  permission?: Permission;
  /** Match the route exactly (so "/" isn't "active" on every page). */
  end?: boolean;
  /** Sub-items rendered indented below this one. */
  children?: NavItem[];
}

const NAV: NavItem[] = [
  { label: "Search", to: "/", icon: Search, permission: "subscribers:read", end: true },
  {
    label: "Routers",
    to: "/routers",
    icon: Router,
    permission: "routers:read",
    children: [
      { label: "PPPoE Sessions", to: "/pppoe-sessions", icon: Users, permission: "subscribers:read" },
    ],
  },
  {
    label: "OLTs",
    to: "/olts",
    icon: Server,
    permission: "olts:read",
    children: [{ label: "OLT Issues", to: "/olt-issues", icon: AlertTriangle, permission: "olts:read" }],
  },
  {
    // Children-only header (Box Chains comes later).
    label: "RADIUS",
    icon: Database,
    children: [
      // "radius:operate" = the NOC-operator RADIUS surface (admin +
      // support). A reseller never gets this — even one with OLT grants —
      // because these pages aren't reseller-scoped server-side.
      { label: "Status & Config", to: "/radius-status", icon: Settings2, permission: "radius:operate" },
      { label: "RADIUS Issues", to: "/radius-issues", icon: AlertTriangle, permission: "radius:operate" },
      { label: "Box Chains", to: "/box-chains", icon: Link2, permission: "radius:operate" },
      { label: "Backup RADIUS", to: "/radius-pools", icon: Boxes, permission: "admin:config" },
    ],
  },
  {
    // Children-only header.
    label: "IP Phone",
    icon: PhoneCall,
    children: [
      { label: "Status & Config", to: "/ip-phone", icon: Settings2, permission: "ipphone:read" },
      { label: "Call Blasting", to: "/call-blast", icon: Megaphone, permission: "callblast:read" },
      { label: "Send SMS", to: "/send-sms", icon: MessagesSquare, permission: "bulksms:send" },
    ],
  },
  {
    // The outside-fibre plant: PSDP / SDP / UDP / FCD / PUDP nodes wired by
    // F-numbered cables. Capacity is the first surface — schematic +
    // issues page follow.
    label: "Fibre",
    icon: Network,
    children: [
      { label: "Capacity", to: "/fiber-capacity", icon: Gauge, permission: "fiber:read" },
      { label: "Schematic", to: "/fiber-schematic", icon: GitBranch, permission: "fiber:read" },
      { label: "Nodes", to: "/fiber-nodes", icon: Boxes, permission: "fiber:read" },
      { label: "Cables", to: "/fiber-cables", icon: Cable, permission: "fiber:read" },
      { label: "Fibre Issues", to: "/fiber-issues", icon: AlertTriangle, permission: "fiber:read" },
    ],
  },
  { label: "SMS & Calls", to: "/sms", icon: MessageSquare, permission: "sms:read" },
  {
    // Children-only header for outreach / customer-engagement surfaces.
    // Social Inbox lives here (the page is the admin-config view for the
    // Messenger + WhatsApp bubble that floats on every page). Future
    // siblings: WhatsApp config, broadcast templates, etc.
    label: "Marketing",
    icon: Sparkles,
    children: [
      { label: "Social Inbox", to: "/messenger", icon: MessageCircle, permission: "admin:config" },
      { label: "Audience Export", to: "/audience-export", icon: FileSpreadsheet, permission: "admin:config" },
    ],
  },
  { label: "Users", to: "/users", icon: Users, permission: "admin:config" },
  { label: "Customer App Logins", to: "/customer-audit", icon: ShieldAlert, permission: "admin:config" },
];

/** The NAV tree filtered to what the current user may see: links they
 *  lack permission for are dropped, and a children-only group header with
 *  no remaining visible children is dropped too. Grant-aware (a reseller
 *  with device grants sees the OLTs / OLT-Issues / Routers items). One
 *  context read; the rest is a pure prune, so it's fine from a component. */
export function useVisibleNav(): NavItem[] {
  const perms = effectivePermissions(useAuth().user);
  const prune = (item: NavItem): NavItem | null => {
    const selfOk = !item.permission || perms.has(item.permission);
    const children = (item.children ?? [])
      .map(prune)
      .filter((x): x is NavItem => x != null);
    if (!item.to) return children.length > 0 ? { ...item, children } : null; // group header
    if (!selfOk) return children.length > 0 ? { ...item, to: undefined, children } : null; // can't open parent → header
    return children.length > 0 ? { ...item, children } : { ...item, children: undefined };
  };
  return NAV.map(prune).filter((x): x is NavItem => x != null);
}
